import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaLeaf, FaChartLine, FaShieldAlt, FaStore, FaWhatsapp } from 'react-icons/fa';
import { getRandomWhatsappNumber } from '../../../utils';

const HeroSection: React.FC = () => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    nomeComercio: '',
    faixaPreco: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Imagem de fundo constante
  const backgroundImage = '/usinas/IMG-20250609-WA0019.webp';

  // Função para formatar número de telefone
  const formatPhoneNumber = (value: string) => {
    const phoneNumber = value.replace(/\D/g, '');
    const limitedPhoneNumber = phoneNumber.slice(0, 11);
    
    if (limitedPhoneNumber.length <= 2) {
      return limitedPhoneNumber;
    } else if (limitedPhoneNumber.length <= 7) {
      return `(${limitedPhoneNumber.slice(0, 2)}) ${limitedPhoneNumber.slice(2)}`;
    } else {
      return `(${limitedPhoneNumber.slice(0, 2)}) ${limitedPhoneNumber.slice(2, 7)}-${limitedPhoneNumber.slice(7)}`;
    }
  };

  // Função para lidar com mudanças nos inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    e.target.setCustomValidity('');
  };

  // Função para lidar com mudanças no select
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    e.target.setCustomValidity('');
  };

  // Função para validação de inputs
  const handleInvalidInput = (e: React.InvalidEvent<HTMLInputElement>) => {
    const { name } = e.target;
    
    if (e.target.validity.valueMissing) {
      switch (name) {
        case 'nome':
          e.target.setCustomValidity('Por favor, preencha seu nome completo.');
          break;
        case 'email':
          e.target.setCustomValidity('Por favor, preencha seu e-mail.');
          break;
        case 'telefone':
          e.target.setCustomValidity('Por favor, preencha seu telefone.');
          break;
        case 'nomeComercio':
          e.target.setCustomValidity('Por favor, preencha o nome do seu comércio.');
          break;
        default:
          e.target.setCustomValidity('Por favor, preencha este campo.');
      }
    } else if (e.target.validity.typeMismatch && name === 'email') {
      e.target.setCustomValidity('Por favor, digite um e-mail válido.');
    }
  };

  // Função para validação do select
  const handleInvalidSelect = (e: React.InvalidEvent<HTMLSelectElement>) => {
    const { name } = e.target;
    
    if (e.target.validity.valueMissing) {
      if (name === 'faixaPreco') {
        e.target.setCustomValidity('Por favor, selecione a faixa de preço.');
      } else {
        e.target.setCustomValidity('Por favor, selecione uma opção.');
      }
    }
  };

  // Função para redirecionar para WhatsApp
  const redirectToWhatsApp = (nome: string = '') => {
    // Dispara evento do Facebook Pixel ao clicar no WhatsApp
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'WhatsappClickHero');
    }
    const whatsappNumber = getRandomWhatsappNumber();
    const message = nome 
      ? `Olá! Meu nome é ${nome} e quero reduzir a conta de energia do meu negócio com a RTL Energia.`
      : 'Olá! Quero reduzir a conta de energia do meu negócio com a RTL Energia.';
    
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/55${whatsappNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };

  // Função para envio do formulário
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const timestamp = new Date().toISOString();
      const atendente = getRandomWhatsappNumber();
      
      const cleanPhone = formData.telefone.replace(/\D/g, '');
      const dataToSend = {
        nome: formData.nome,
        telefone: cleanPhone,
        email: formData.email,
        nomeComercio: formData.nomeComercio,
        faixaPreco: formData.faixaPreco,
        timestamp: timestamp,
        atendente: atendente
      };

      console.log('Enviando dados:', dataToSend);

      const n8nUrl = process.env.REACT_APP_N8N_URL || '';
      console.log('N8N URL:', n8nUrl);
      const response = await fetch(n8nUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        redirectToWhatsApp(formData.nome);
        
        setFormData({
          nome: '',
          email: '',
          telefone: '',
          nomeComercio: '',
          faixaPreco: ''
        });
      } else {
        throw new Error('Erro ao enviar dados');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      redirectToWhatsApp(formData.nome);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="hero"
      className="relative min-h-[500px] lg:min-h-[75vh] flex items-center pt-16 pb-10 lg:pt-20 lg:pb-16 overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 bg-[#04102A] overflow-hidden">
        <img
          src={backgroundImage}
          alt="Usina Solar RTL Energia"
          className="w-full h-full object-cover"
          loading="eager"
          style={{
            filter: 'brightness(0.85) contrast(1.15) saturate(1.1)',
          }}
        />
        <div className="absolute inset-0 bg-[#04102A]/65"></div>
      </div>

      {/* Gradientes adicionais */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#04102A]/45 via-transparent to-[#04102A]/25 z-[5]"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#04102A]/15 via-transparent to-[#04102A]/35 z-[5]"></div>
      
      <div className="container relative z-10 px-4 mx-auto mt-8 sm:mt-0 overflow-visible">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center overflow-visible relative">
          {/* Conteúdo à esquerda */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-white overflow-visible relative z-20 lg:-mt-6 bg-left-hero rounded-sm lg:rounded-r-none lg:rounded-l-sm p-6 lg:pr-0"
          >
            <span className="inline-block bg-secondary text-primary font-semibold px-3 py-2 rounded-sm mb-3">
              Zero investimento inicial
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
              Economize até <span className="text-secondary drop-shadow-lg filter brightness-110">25%</span> logo na sua próxima fatura
            </h1>
            <p className="text-xl mb-6 text-white opacity-90">
            Você recebe a energia direto das nossas usinas solares. Sem instalações. Sem mensalidades. <strong>Nós cuidamos de tudo.</strong>
            </p>
            
            {/* Benefícios em ícones */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-sm bg-secondary/20 flex items-center justify-center shrink-0">
                  <FaLeaf className="text-secondary" />
                </div>
                <div>
                  <span className="text-white text-sm font-semibold block">Energia 100% limpa</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-sm bg-secondary/20 flex items-center justify-center shrink-0">
                  <FaShieldAlt className="text-secondary" />
                </div>
                <div>
                  <span className="text-white text-sm font-semibold block">Proteção contra bandeiras</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-sm bg-secondary/20 flex items-center justify-center shrink-0">
                  <FaChartLine className="text-secondary" />
                </div>
                <div>
                  <span className="text-white text-sm font-semibold block">Economia garantida</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-sm bg-secondary/20 flex items-center justify-center shrink-0">
                  <FaStore className="text-secondary" />
                </div>
                <div>
                  <span className="text-white text-sm font-semibold block">Rede elétrica 100% estável</span>
                </div>
              </div>
            </div>
            
            {/* Formulário para mobile */}
            <div className="block lg:hidden mt-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-white/10 backdrop-blur-sm border border-white/20 p-6 rounded-sm"
              >
                <h3 className="text-xl font-bold text-white mb-4 text-center">
                  Fale conosco ainda hoje!
                </h3>
                <form onSubmit={handleFormSubmit} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      name="nome"
                      placeholder="Nome completo"
                      value={formData.nome}
                      onChange={handleInputChange}
                      onInvalid={handleInvalidInput}
                      required
                      className="w-full px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                    />
                  </div>
                  <div>
                                         <input
                       type="text"
                       name="nomeComercio"
                       placeholder="Nome do comércio"
                       value={formData.nomeComercio}
                       onChange={handleInputChange}
                       onInvalid={handleInvalidInput}
                       required
                       className="w-full px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                     />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="E-mail"
                      value={formData.email}
                      onChange={handleInputChange}
                      onInvalid={handleInvalidInput}
                      required
                      className="w-full px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="telefone"
                      placeholder="(11) 99999-9999"
                      value={formData.telefone}
                      onChange={handleInputChange}
                      onInvalid={handleInvalidInput}
                      maxLength={15}
                      required
                      className="w-full px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                    />
                  </div>
                  <div>
                                         <select
                       name="faixaPreco"
                       value={formData.faixaPreco}
                       onChange={handleSelectChange}
                       onInvalid={handleInvalidSelect}
                       required
                       className="w-full px-4 py-2 rounded-sm border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
                     >
                      <option value="">Custo mensal com energia </option>
                      <option value="Abaixo de R$ 7 mil">Abaixo de R$ 7 mil</option>
                      <option value="De R$ 7 mil a R$ 15 mil">De R$ 7 mil a R$ 15 mil</option>
                      <option value="De R$ 15 mil a R$ 30 mil">De R$ 15 mil a R$ 30 mil</option>
                      <option value="Acima de R$ 30 mil">Acima de R$ 30 mil</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-secondary hover:bg-secondary/90 text-primary font-bold py-2.5 px-6 rounded-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    <FaWhatsapp className="text-xl" />
                    {isSubmitting ? 'Enviando...' : 'Quero economizar!'}
                  </button>
                </form>
              </motion.div>
            </div>
          </motion.div>

          {/* Formulário à direita (apenas desktop) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="hidden lg:block relative overflow-visible z-10 bg-right-hero rounded-sm lg:rounded-l-none lg:rounded-r-sm p-6 lg:pl-0"
          >
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 p-8 rounded-sm shadow-xl h-full">
              <h3 className="text-3xl font-bold text-white mb-6 text-center">
                Fale conosco ainda hoje!
              </h3>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-white mb-2">
                    Nome completo
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    placeholder="Digite seu nome completo"
                    value={formData.nome}
                    onChange={handleInputChange}
                    onInvalid={handleInvalidInput}
                    required
                    className="w-full px-4 py-3 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                  />
                </div>
                <div>
                  <label htmlFor="nomeComercio" className="block text-sm font-medium text-white mb-2">
                    Nome do comércio
                  </label>
                                     <input
                     type="text"
                     id="nomeComercio"
                     name="nomeComercio"
                     placeholder="Digite o nome do seu comércio"
                     value={formData.nomeComercio}
                     onChange={handleInputChange}
                     onInvalid={handleInvalidInput}
                     required
                     className="w-full px-4 py-3 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                   />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    E-mail
                  </label>
                                      <input
                     type="email"
                     id="email"
                     name="email"
                     placeholder="Digite seu e-mail"
                     value={formData.email}
                     onChange={handleInputChange}
                     onInvalid={handleInvalidInput}
                     required
                     className="w-full px-4 py-3 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                   />
                </div>
                <div>
                  <label htmlFor="telefone" className="block text-sm font-medium text-white mb-2">
                    Telefone
                  </label>
                                      <input
                     type="tel"
                     id="telefone"
                     name="telefone"
                     placeholder="(11) 99999-9999"
                     value={formData.telefone}
                     onChange={handleInputChange}
                     onInvalid={handleInvalidInput}
                     maxLength={15}
                     required
                     className="w-full px-4 py-3 rounded-sm border border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                   />
                </div>
                <div>
                  <label htmlFor="faixaPreco" className="block text-sm font-medium text-white mb-2">
                    Faixa de preço mensal com energia
                  </label>
                                     <select
                     id="faixaPreco"
                     name="faixaPreco"
                     value={formData.faixaPreco}
                     onChange={handleSelectChange}
                     onInvalid={handleInvalidSelect}
                     required
                     className="w-full px-4 py-3 rounded-sm border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent text-lg"
                   >
                    <option value="">Selecione</option>
                    <option value="Abaixo de R$ 7 mil">Abaixo de R$ 7 mil</option>
                    <option value="De R$ 7 mil a R$ 15 mil">De R$ 7 mil a R$ 15 mil</option>
                    <option value="De R$ 15 mil a R$ 30 mil">De R$ 15 mil a R$ 30 mil</option>
                    <option value="Acima de R$ 30 mil">Acima de R$ 30 mil</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-secondary hover:bg-secondary/90 text-primary font-bold py-3 px-6 rounded-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  <FaWhatsapp className="text-xl" />
                  {isSubmitting ? 'Enviando...' : 'Enviar mensagem'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection; 